import React from 'react';
import { Link } from 'react-router-dom';
import { Check, QrCode, CreditCard, Shield } from 'lucide-react';
import { pricing } from '@config/pricing';

const PricingSection: React.FC = () => {
    return (
        <section className="py-24 relative overflow-hidden z-10">
            <div className="container mx-auto px-6 relative">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl lg:text-6xl font-black font-serif text-white mb-6">
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">Simple Pricing</span>, No Subscriptions
                        </h2>
                        <p className="text-lg md:text-xl text-primary-200/80 font-sans max-w-2xl mx-auto">Pay only {pricing.currency}{pricing.quick} per message. No hidden fees, no commitments, credits never expire.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 items-stretch">
                        {/* Pricing Card - Matches Royal Purple Theme */}
                        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-cosmic-800 to-cosmic-950 p-10 border border-white/20 shadow-2xl flex flex-col group hover:scale-[1.02] transition-transform duration-500">
                            {/* Badge */}
                            <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/10 text-xs font-bold uppercase tracking-widest text-white font-sans">
                                Pay As You Go
                            </div>

                            <div className="mb-8">
                                <span className="text-6xl font-black text-white font-sans">{pricing.currency}{pricing.quick}</span>
                                <span className="text-lg text-primary-200/60 ml-2 font-sans">/ message</span>
                            </div>

                            <div className="space-y-4 mb-10 flex-1">
                                <div className="flex items-center gap-3 text-white font-medium font-sans"><Check className="text-emerald-400 shrink-0" size={20} /> All spiritual services included</div>
                                <div className="flex items-center gap-3 text-white font-medium font-sans"><Check className="text-emerald-400 shrink-0" size={20} /> No monthly subscriptions</div>
                                <div className="flex items-center gap-3 text-white font-medium font-sans"><Check className="text-emerald-400 shrink-0" size={20} /> Credits never expire</div>
                                <div className="flex items-center gap-3 text-white font-medium font-sans"><Check className="text-emerald-400 shrink-0" size={20} /> Pay only for what you use</div>
                                <div className="flex items-center gap-3 text-yellow-300 font-bold font-sans"><Check className="text-yellow-300 shrink-0" size={20} /> {pricing.currency}{pricing.signupReward} free on signup!</div>
                            </div>

                            <Link
                                to="/login"
                                className="w-full py-4 bg-white text-cosmic-900 font-black rounded-xl hover:bg-secondary-50 transition-colors text-center uppercase tracking-widest text-sm mb-6 font-sans"
                            >
                                Start Chatting Now
                            </Link>
                            <p className="text-center text-xs text-white/50 font-sans">*{pricing.freeQuestions} Free Messages to explore your cosmic destiny!</p>
                        </div>

                        {/* What's Included / Features */}
                        <div className="bg-white/[0.08] border border-white/20 rounded-[2.5rem] p-10 flex flex-col">
                            <h3 className="text-2xl font-bold text-white font-sans mb-6">What Each Message Includes:</h3>
                            <div className="space-y-4 mb-10 flex-1">
                                <div className="flex items-start gap-4">
                                    <div className="p-2 rounded-lg bg-primary-500/20 text-primary-300 shrink-0"><Check size={18} /></div>
                                    <div>
                                        <h4 className="text-white font-bold text-sm font-sans">Interactive AI Spiritual Guidance</h4>
                                        <p className="text-xs text-primary-200/60 mt-1">Real-time conversation with our Bhrigu Rishi AI.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="p-2 rounded-lg bg-primary-500/20 text-primary-300 shrink-0"><Check size={18} /></div>
                                    <div>
                                        <h4 className="text-white font-bold text-sm font-sans">Vedic Astrology & Birth Chart</h4>
                                        <p className="text-xs text-primary-200/60 mt-1">Detailed analysis of planetary positions and dashas.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="p-2 rounded-lg bg-primary-500/20 text-primary-300 shrink-0"><Check size={18} /></div>
                                    <div>
                                        <h4 className="text-white font-bold text-sm font-sans">Tarot & Numerology</h4>
                                        <p className="text-xs text-primary-200/60 mt-1">Card interpretations and number symbolism.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="p-2 rounded-lg bg-primary-500/20 text-primary-300 shrink-0"><Check size={18} /></div>
                                    <div>
                                        <h4 className="text-white font-bold text-sm font-sans">Palm & Face Reading</h4>
                                        <p className="text-xs text-primary-200/60 mt-1">Insights derived from physical features.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white/[0.08] p-4 rounded-xl text-center border border-white/20">
                                    <div className="text-secondary-400 font-black text-xl mb-1 font-sans">No Expiry</div>
                                    <div className="text-[10px] text-primary-200/60 font-sans">Credits valid forever</div>
                                </div>
                                <div className="bg-white/[0.08] p-4 rounded-xl text-center border border-white/20">
                                    <div className="text-secondary-400 font-black text-xl mb-1 font-sans">Flexible</div>
                                    <div className="text-[10px] text-primary-200/60 font-sans">Top-up from ₹5</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Methods */}
                    <div className="mt-16 bg-white/[0.08] border border-white/20 rounded-3xl p-8">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                            <div className="text-center md:text-left">
                                <h3 className="text-xl font-bold text-white font-sans mb-2">Secure Payments</h3>
                                <p className="text-sm text-primary-200/70 font-sans">Safe processing with multiple options.</p>
                            </div>

                            <div className="flex flex-wrap justify-center gap-6 items-center flex-1">
                                {/* UPI */}
                                <div className="flex flex-col items-center gap-2">
                                    <div className="h-10 px-4 flex items-center justify-center bg-white rounded-lg text-cosmic-900 font-bold text-xs gap-1 font-sans">
                                        <QrCode size={16} /> BHIM UPI
                                    </div>
                                    <span className="text-[10px] text-primary-300 uppercase tracking-widest font-bold font-sans">UPI Apps</span>
                                </div>

                                {/* Cards */}
                                <div className="flex flex-col items-center gap-2">
                                    <div className="h-10 px-4 flex items-center justify-center bg-white rounded-lg text-cosmic-900 font-bold text-xs gap-1 font-sans">
                                        <CreditCard size={16} /> CARDS
                                    </div>
                                    <span className="text-[10px] text-primary-300 uppercase tracking-widest font-bold font-sans">Credit/Debit</span>
                                </div>

                                {/* Net Banking */}
                                <div className="flex flex-col items-center gap-2">
                                    <div className="h-10 px-4 flex items-center justify-center bg-white rounded-lg text-cosmic-900 font-bold text-xs gap-1 font-sans">
                                        <Shield size={16} /> BANKS
                                    </div>
                                    <span className="text-[10px] text-primary-300 uppercase tracking-widest font-bold font-sans">Net Banking</span>
                                </div>
                            </div>

                            <div className="flex gap-4 text-xs text-emerald-400 font-bold items-center bg-emerald-900/20 px-4 py-2 rounded-full border border-emerald-500/20 font-sans">
                                <span className="flex items-center gap-1"><Shield size={12} /> SSL Encrypted</span>
                                <span className="flex items-center gap-1"><Check size={12} /> PCI Compliant</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default PricingSection;
